import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUtensils, 
  faPizzaSlice, 
  faWineGlass,
  faClock,
  faCrown
} from '@fortawesome/free-solid-svg-icons';
import ParallaxBanner from '../../common/Parralax-banner';
import Footer from '../../Footer/Footer';
import Button from '../../Button/Button';
import retailBanner from '../../../assets/banner/retailBan3.webp';
import bistroImage from '../../../assets/bistro.webp';
import pizzaImage from '../../../assets/pizza.webp';
import barImage from '../../../assets/ballonBar.webp';


const DiningContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  background-color: ${props => props.active ? props.theme.colors.accent : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background-color: ${props => props.active ? props.theme.colors.accent : 'rgba(0, 0, 0, 0.05)'};
  }

  svg {
    font-size: 1.2rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const IntroTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.body};
  color: ${props => props.theme.colors.secondary};
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
`;

const VenuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const VenueCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
`;

const VenueImage = styled.div`
  height: 250px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const VenueContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const VenueTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    font-size: 1.5rem;
  }
`;

const VenueDescription = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const VenueHours = styled.div`
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const VenueButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const StyledButton = styled(Button)`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: 2px solid ${props => props.theme.colors.accent};
  padding: 0.6rem 1.5rem;
  border-radius: 50px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: auto;
  text-align: center;

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
  }
`;

const RetailDining = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const venues = [
    {
      title: "Belvoir Bistro",
      description: "Modern British cuisine in elegant surroundings. Enjoy locally sourced ingredients and seasonal menus crafted by our expert chefs.",
      image: bistroImage,
      icon: faUtensils,
      hours: "Daily · 10:00 - 17:00",
      path: "/retail-village/dining/belvoir-bistro"
    },
    {
      title: "Woodfired Pizza",
      description: "Authentic Italian pizzas cooked in our traditional woodfired oven. Fresh ingredients, handmade dough, and classic toppings.",
      image: pizzaImage,
      icon: faPizzaSlice,
      hours: "Daily · 10:00 - 17:00",
      path: "/retail-village/dining/woodfired-pizza"
    },
    {
      title: "Balloon Bar",
      description: "A unique bar experience with craft beers, fine wines, and signature cocktails. Perfect for a relaxing afternoon drink.",
      image: barImage,
      icon: faWineGlass,
      hours: "Check page for opening times",
      path: "/retail-village/dining/balloon-bar"
    }
  ];

  return (
    <DiningContainer>
      <ParallaxBanner 
        title="Dining at Belvoir"
        subtitle="From casual bites to refined dining experiences"
        image={retailBanner}
      />
      <ContentContainer>
        <TabContainer>
          <Tab 
            active={location.pathname === '/retail-village/dining'}
            onClick={() => navigate('/retail-village/dining')}
          >
            <FontAwesomeIcon icon={faUtensils} />
            Retail Dining
          </Tab>
          <Tab 
            active={location.pathname === '/castle-dining'}
            onClick={() => navigate('/castle-dining')}
          >
            <FontAwesomeIcon icon={faCrown} />
            Castle Dining
          </Tab>
        </TabContainer>
        <IntroSection>
          <IntroTitle>Our Dining Venues</IntroTitle>
          <IntroText>
            Discover a variety of dining experiences at Belvoir Castle. From casual woodfired pizzas to elegant bistro dining, 
            each venue offers its own unique atmosphere and culinary delights. Whether you're looking for a quick bite or 
            a leisurely meal, our restaurants are ready to serve you.
          </IntroText>
        </IntroSection>
        <VenuesGrid>
          {venues.map((venue, index) => (
            <VenueCard key={index}>
              <VenueImage image={venue.image} />
              <VenueContent>
                <VenueTitle>
                  <FontAwesomeIcon icon={venue.icon} />
                  {venue.title}
                </VenueTitle>
                <VenueDescription>{venue.description}</VenueDescription>
                <VenueHours>
                  <FontAwesomeIcon icon={faClock} />
                  {venue.hours}
                </VenueHours>
                <VenueButtonContainer>
                  <StyledButton onClick={() => navigate(venue.path)}>
                    Learn More
                  </StyledButton>
                </VenueButtonContainer>
              </VenueContent>
            </VenueCard>
          ))}
        </VenuesGrid>
      </ContentContainer>
      <Footer />
    </DiningContainer>
  );
};

export default RetailDining;
